import { useState, useCallback } from 'react';
import type {
  DeployFailureContext,
  DeployFailureJob,
} from '@volley/crucible-ipc-types/ipc-channels';
import { useFixInAgent } from '../hooks/useDeployFailure';
import type { DeployActionState } from '../workspace/deploy-action-state';

/**
 * Issue #277 — `<DeployFailureCard>` is the renderer half of the
 * deploy-failure surfacing flow. When the existing `publish:run` IPC
 * rejects, the Workspace fetches a `DeployFailureContext` via
 * `deploy:get-failure-context` and (when the result is a `failure`)
 * mounts this card in place of the legacy "deploy failed" toast.
 *
 * Three CTAs:
 *   - **Fix in agent** — calls `deploy:fix-in-agent` to format the
 *     annotations into a structured chat prompt, then dispatches the
 *     returned message via the `onFixInAgent` callback. The chat
 *     reducer treats it identically to the user typing the message.
 *   - **View on GitHub** — opens the workflow run URL in the user's
 *     default browser (via `window.open`, same shape `<BugDraftCard>`
 *     uses for "Open issue").
 *   - **Retry deploy** — fires the parent's `onRetry` callback
 *     which kicks off a fresh `publish:run` against the latest
 *     `main`. We deliberately do NOT use GitHub's
 *     `rerun-failed-jobs` endpoint (Cursor Bugbot round 1):
 *     `rerun-failed-jobs` re-runs the original failed run at the
 *     ORIGINAL SHA, so after the agent pushed a fix the retry
 *     would still execute the broken pre-fix code. Re-running
 *     publish:run pushes the latest local commits (no-op when
 *     already up to date) and triggers a workflow run on the
 *     fresh SHA — which is what the user actually wants.
 *
 * The card auto-dismisses (via the parent flipping
 * `enableDeployFailure=false`) when the user clicks "Dismiss" or
 * navigates away. We don't poll — the deploy flow itself is
 * action-driven, and a passive poll on every mount would burn GitHub
 * rate-limit budget for the common case (no recent failure).
 */
export interface DeployFailureCardProps {
  gameId: string;
  context: DeployFailureContext;
  /**
   * Called with the structured prompt the agent should receive when
   * the user clicks "Fix in agent". The Workspace dispatches this
   * via `chatDispatch({ type: 'user/type', text }) +
   * chatDispatch({ type: 'user/send' })`. Wired this way (rather
   * than the card reaching into chatDispatch directly) so the card
   * stays decoupled from the chat reducer for testability.
   */
  onFixInAgent: (message: string) => void;
  /** Called when the user clicks Dismiss — parent unmounts the card. */
  onDismiss: () => void;
  /**
   * Called when the user clicks "Retry deploy". Parent wires this
   * to the existing deploy handler (`handleDeploy` in Workspace),
   * which fires a fresh `publish:run` against the latest local
   * commits — the only way to retry that picks up an agent's fix.
   */
  onRetry: () => void;
  actionState?: DeployActionState;
  hasAnthropicKey?: boolean;
}

export function DeployFailureCard({
  gameId,
  context,
  onFixInAgent,
  onDismiss,
  onRetry,
  actionState,
  hasAnthropicKey = true,
}: DeployFailureCardProps): React.JSX.Element | null {
  const { fixInAgent } = useFixInAgent();
  const [pending, setPending] = useState<null | 'fix'>(null);

  // Hooks first, branches after. The early-return for non-failure
  // contexts MUST live below all useState / useCallback calls so a
  // context change between renders (e.g. failure → no-failure after
  // a successful retry) doesn't change the hook count and trip the
  // rules-of-hooks check. We snapshot the workflowRun / failedJobs
  // up-front so the closures above don't dereference an undefined
  // when the parent passes a non-failure context.
  const workflowRun = context.workflowRun;
  const failedJobs = context.failedJobs ?? [];
  const githubUrl = workflowRun?.htmlUrl ?? context.failedRunUrl ?? failedJobs[0]?.htmlUrl;
  const canFixInAgent = hasAnthropicKey && failedJobs.length > 0;
  const primaryCta =
    actionState?.failurePrimaryCta ?? (canFixInAgent ? 'fix-in-agent' : 'retry-deploy');

  const handleFixInAgent = useCallback(async () => {
    setPending('fix');
    try {
      const result = await fixInAgent({ gameId, context });
      if (result.ok && result.message) {
        onFixInAgent(result.message);
      }
    } finally {
      setPending(null);
    }
  }, [context, fixInAgent, gameId, onFixInAgent]);

  const handleViewOnGithub = useCallback(() => {
    if (!githubUrl) return;
    // electron-renderer + Vite both expose `window.open`; main's
    // default-handler maps it to `shell.openExternal` for `https://`
    // origins so this lands in the user's default browser rather
    // than spawning a new BrowserWindow.
    window.open(githubUrl, '_blank', 'noreferrer');
  }, [githubUrl]);

  // Defensive: only render on `failure`. The Workspace already
  // filters but a direct test-harness caller could land here with a
  // `no-failure` / `error` context.
  if (context.status !== 'failure') return null;

  return (
    <div
      data-testid="deploy-failure-card"
      className="mb-3 rounded-lg border border-red-500/30 bg-red-500/5 p-4"
      role="alert"
    >
      <div className="mb-2 flex items-center justify-between">
        <span
          className="text-xs font-medium uppercase tracking-wider text-red-300/80"
          data-testid="deploy-failure-card-header"
        >
          Deploy failed
        </span>
        <button
          type="button"
          data-testid="deploy-failure-dismiss"
          onClick={onDismiss}
          className="text-xs text-white/40 transition-colors hover:text-white/70"
          aria-label="Dismiss deploy failure"
        >
          Dismiss
        </button>
      </div>

      {workflowRun && (
        <div className="mb-3 text-xs text-white/60">
          Workflow run{' '}
          <a
            href={workflowRun.htmlUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="deploy-failure-run-link"
            className="font-mono text-white/80 underline-offset-2 hover:underline"
          >
            #{workflowRun.id}
          </a>
          {workflowRun.headSha && (
            <span className="ml-2 font-mono text-[10px] text-white/40">
              {workflowRun.headSha.slice(0, 7)}
            </span>
          )}
        </div>
      )}

      {failedJobs.length === 0 && (
        <div
          data-testid="deploy-failure-no-jobs"
          className="mb-3 rounded border border-white/10 bg-black/20 p-2 text-xs text-white/60"
        >
          The deploy failed but Foundry could not enumerate the failed jobs. Open the workflow run
          on GitHub for the full output.
          {context.errorMessage && (
            <div className="mt-1 text-[10px] text-white/40">{context.errorMessage}</div>
          )}
        </div>
      )}

      {failedJobs.map((job) => (
        <DeployFailureJobBlock key={job.id} job={job} runHtmlUrl={workflowRun?.htmlUrl} />
      ))}

      {context.fallbackLogs && (
        <details
          data-testid="deploy-failure-fallback-logs"
          className="mb-3 rounded border border-white/10 bg-black/20 p-2"
        >
          <summary className="cursor-pointer text-[11px] text-white/60">
            Log tail (no annotations available — common for build / deploy step failures)
          </summary>
          <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] text-white/70">
            {context.fallbackLogs}
          </pre>
        </details>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {githubUrl && (
          <button
            type="button"
            data-testid="deploy-failure-view-on-github"
            onClick={handleViewOnGithub}
            className="rounded border border-white/15 px-3 py-1 text-xs text-white/70 transition-colors hover:bg-white/5 disabled:opacity-40"
          >
            View on GitHub
          </button>
        )}
        {primaryCta === 'fix-in-agent' && (
          <button
            type="button"
            data-testid="deploy-failure-retry"
            onClick={onRetry}
            disabled={pending !== null}
            className="rounded border border-white/15 px-3 py-1 text-xs text-white/70 transition-colors hover:bg-white/5 disabled:opacity-40"
          >
            Retry deploy
          </button>
        )}
        {hasAnthropicKey && (
          <button
            type="button"
            data-testid="deploy-failure-fix-in-agent"
            onClick={() => {
              void handleFixInAgent();
            }}
            disabled={pending !== null || !canFixInAgent}
            className={
              primaryCta === 'fix-in-agent'
                ? 'rounded border border-amber-500/40 bg-amber-500/20 px-3 py-1 text-xs font-medium text-amber-100 transition-colors hover:bg-amber-500/30 disabled:opacity-50'
                : 'rounded border border-white/15 px-3 py-1 text-xs text-white/70 transition-colors hover:bg-white/5 disabled:opacity-40'
            }
          >
            {pending === 'fix' ? 'Sending…' : 'Fix in agent'}
          </button>
        )}
        {primaryCta === 'retry-deploy' && (
          <button
            type="button"
            data-testid="deploy-failure-retry"
            onClick={onRetry}
            disabled={pending !== null}
            className="rounded border border-amber-500/40 bg-amber-500/20 px-3 py-1 text-xs font-medium text-amber-100 transition-colors hover:bg-amber-500/30 disabled:opacity-50"
          >
            Retry deploy
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * One block per failed job. Annotations are capped at 5 visible per
 * job in the card — the IPC payload already limits to 50 server-side,
 * but 50 errors in a card is unscannable. The "+ N more" footer
 * points the user at the workflow run URL for the full list. (The
 * Fix-in-agent prompt sends 10 per job, since the agent doesn't have
 * the same scannability concern.)
 */
const VISIBLE_ANNOTATIONS_PER_JOB = 5;

function DeployFailureJobBlock({
  job,
  runHtmlUrl,
}: {
  job: DeployFailureJob;
  runHtmlUrl?: string;
}): React.JSX.Element {
  const visible = job.annotations.slice(0, VISIBLE_ANNOTATIONS_PER_JOB);
  const totalErrors = job.annotations.length + job.truncated;
  const moreCount =
    Math.max(0, job.annotations.length - VISIBLE_ANNOTATIONS_PER_JOB) + job.truncated;
  const totalLabel =
    totalErrors === 1 ? '1 error' : `${totalErrors} error${totalErrors === 1 ? '' : 's'}`;

  return (
    <div
      data-testid={`deploy-failure-job-${job.name}`}
      className="mb-3 rounded border border-white/10 bg-black/20 p-2"
    >
      <div className="mb-1 flex items-center justify-between">
        <span className="font-mono text-xs text-white/80">{job.name}</span>
        <span className="text-[10px] text-white/40">{totalLabel}</span>
      </div>
      {/* Issue #408 — surface the failed step name prominently
          BEFORE the annotation list (or the "no annotations"
          fallback). The step name is the load-bearing answer to
          "what actually broke" — even when annotations are
          present, the step gives one-glance context (e.g. "the
          Register step in the deploy job", not "build-and-deploy
          failed somewhere"). */}
      {job.failedStep && (
        <div
          data-testid={`deploy-failure-job-step-${job.name}`}
          className="mb-1 text-[11px] text-white/70"
        >
          <span className="text-white/40">Failed step:</span>{' '}
          <span className="font-mono text-amber-200/90">{job.failedStep}</span>
        </div>
      )}
      {visible.length === 0 ? (
        <div className="text-[11px] text-white/40">
          No annotations available. Check the job log on GitHub for details.
        </div>
      ) : (
        <ul className="space-y-1">
          {visible.map((a, i) => (
            <li
              key={`${a.path}-${a.startLine}-${i}`}
              className="font-mono text-[11px] text-white/70"
            >
              <span className="text-amber-300/80">
                {a.path}
                {a.startLine > 0 ? `:${a.startLine}` : ''}
              </span>{' '}
              <span className="text-white/60">— {a.message.split('\n')[0]}</span>
            </li>
          ))}
        </ul>
      )}
      {/* Issue #408 — focused log window around the
          `##[error]Process completed with exit code N` marker on
          the failed step. Distinct from the larger
          `<DeployFailureCard>`-level `fallbackLogs` band: this is
          per-job and ONLY shows the step that broke. Collapsed
          by default — many failures are obvious from the step
          name + annotations alone, and burying the log block
          behind a chevron keeps the card scannable. */}
      {job.failedStepLogTail && (
        <details
          data-testid={`deploy-failure-job-log-${job.name}`}
          className="mt-2 rounded border border-white/10 bg-black/30 p-1.5"
        >
          <summary className="cursor-pointer text-[10px] text-white/50">Log around error</summary>
          <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px] text-white/60">
            {job.failedStepLogTail}
          </pre>
        </details>
      )}
      {moreCount > 0 && runHtmlUrl && (
        <a
          href={runHtmlUrl}
          target="_blank"
          rel="noreferrer"
          data-testid={`deploy-failure-job-more-${job.name}`}
          className="mt-1 inline-block text-[10px] text-white/40 underline-offset-2 hover:underline"
        >
          + {moreCount} more on GitHub
        </a>
      )}
    </div>
  );
}
